import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchBillingPlans, fetchBillingMe, createCheckoutSession, fetchCheckoutStatus, cancelSubscription,
  startFreeTrial, createPortalSession,
} from "@/lib/api";
import { Crown, Check, Sparkles, ArrowRight, ShieldCheck, RefreshCcw, Clock, CreditCard } from "lucide-react";

const PAYWALL = {
  page: "paywall-page",
  planCard: "paywall-plan-card",
  checkoutBtn: "paywall-checkout",
  cancelBtn: "paywall-cancel",
  startTrialBtn: "paywall-start-trial",
  portalBtn: "paywall-portal",
};

const FEATURE_HEADLINES = [
  "Unlimited Children",
  "Family Insights",
  "Health Trends",
  "Priority Alerts",
  "Future Growth Journey Features",
];

export default function PaywallPage() {
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const sessionIdFromQs = searchParams.get("stripe_session_id");

  const load = async () => {
    const [p, m] = await Promise.all([fetchBillingPlans(), fetchBillingMe()]);
    setPlans(p.plans);
    setTrialDays(p.trial_days);
    setMe(m);
  };

  useEffect(() => { load(); }, []);

  // After checkout redirect: poll status
  useEffect(() => {
    if (!sessionIdFromQs) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const status = await fetchCheckoutStatus(sessionIdFromQs);
        if (status.payment_status === "paid") {
          toast.success("Payment successful! You are now Family Plus.");
          load();
          return;
        }
        if (status.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          return;
        }
        if (attempts < 5) setTimeout(poll, 2000);
      } catch (e) {
        toast.error("Could not confirm payment status.");
      }
    };
    poll();
  }, [sessionIdFromQs]);

  const handleCheckout = async (planId) => {
    setBusy(true);
    try {
      const { url } = await createCheckoutSession(planId);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel Family Plus? You'll return to the Free plan.")) return;
    setBusy(true);
    try {
      await cancelSubscription();
      toast.success("Subscription canceled. You're now on Free.");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleStartTrial = async () => {
    setBusy(true);
    try {
      const r = await startFreeTrial();
      if (r.status === "noop") {
        toast.message("You already have Family Plus access.");
      } else {
        toast.success("14-day free trial started — enjoy Family Plus.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial.");
    } finally {
      setBusy(false);
    }
  };

  const handlePortal = async () => {
    setBusy(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not open billing portal.";
      toast.error(detail);
      setBusy(false);
    }
  };

  if (!plans.length || !me) return <div className="text-[var(--fcc-muted)]">Loading plans…</div>;

  const isPaid = me.is_paid_active;
  const paidPlans = plans.filter(p => p.id !== "free");

  return (
    <div data-testid={PAYWALL.page} className="space-y-10">
      <header className="fcc-card overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="fcc-pill fcc-chip-emma"><Crown className="w-3 h-3" /> KidHQ Plans</span>
            {isPaid && <span className="fcc-pill bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]">{me.effective_plan.name}</span>}
          </div>
          <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-6">
            Upgrade to Family Plus
          </h1>
          <p className="text-[var(--fcc-ink-soft)] mt-5 max-w-2xl leading-relaxed">
            One AI Command Center for every kid in your family. Cancel any time. First {trialDays} days free.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-2.5 max-w-xl">
            {FEATURE_HEADLINES.map(f => (
              <li key={f} className="flex items-center gap-2 text-sm text-[var(--fcc-ink)]">
                <Check className="w-4 h-4 text-[var(--fcc-green)]" /> {f}
              </li>
            ))}
          </ul>
          {!isPaid && (
            <button
              data-testid={PAYWALL.startTrialBtn}
              onClick={handleStartTrial}
              disabled={busy}
              className="fcc-btn-primary mt-7 disabled:opacity-50"
            >
              <Clock className="w-4 h-4" /> Start 14-day free trial
            </button>
          )}
          {isPaid && (
            <button
              data-testid={PAYWALL.portalBtn}
              onClick={handlePortal}
              disabled={busy}
              className="fcc-btn-outline mt-7 disabled:opacity-50"
            >
              <CreditCard className="w-4 h-4" /> Manage billing
            </button>
          )}
        </div>
        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
          <p className="label-overline flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Secure checkout by Stripe</p>
          <p className="text-sm text-[var(--fcc-ink-soft)] mt-3">All major cards. No setup fees. Powered by All IT Solutions LLC.</p>
        </aside>
      </header>

      <section className="grid md:grid-cols-3 gap-5">
        {plans.map(p => {
          const active = me.plan === p.id && isPaid;
          const isFree = p.id === "free";
          const annual = p.id === "family_plus_annual";
          return (
            <article
              key={p.id}
              data-testid={`${PAYWALL.planCard}-${p.id}`}
              className={`fcc-card p-7 flex flex-col ${annual ? "ring-2 ring-[var(--fcc-green)] relative" : ""}`}
            >
              {annual && (
                <span className="absolute -top-3 left-7 fcc-pill bg-[var(--fcc-green)] text-white">
                  <Sparkles className="w-3 h-3" /> Best value
                </span>
              )}
              <h3 className="font-display text-2xl text-[var(--fcc-ink)]">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl text-[var(--fcc-ink)]">
                  {isFree ? "$0" : `$${p.amount_usd}`}
                </span>
                <span className="text-[var(--fcc-muted)] text-sm mb-2">
                  {isFree ? "forever" : p.interval === "year" ? "/yr" : "/mo"}
                </span>
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map(f => (
                  <li key={f} className="text-sm text-[var(--fcc-ink-soft)] flex items-start gap-2">
                    <Check className="w-4 h-4 text-[var(--fcc-green)] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {isFree ? (
                  <button className="fcc-btn-outline w-full" disabled>
                    {me.plan === "free" || !isPaid ? "Your current plan" : "Downgrade via cancel"}
                  </button>
                ) : active ? (
                  <button className="fcc-btn-outline w-full" disabled>
                    <Check className="w-4 h-4" /> Active
                  </button>
                ) : (
                  <button
                    data-testid={`${PAYWALL.checkoutBtn}-${p.id}`}
                    onClick={() => handleCheckout(p.id)}
                    disabled={busy}
                    className="fcc-btn-primary w-full disabled:opacity-50"
                  >
                    {busy ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                    Upgrade to {p.name} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {isPaid && (
        <div className="fcc-card p-6 text-right">
          <button
            data-testid={PAYWALL.cancelBtn}
            onClick={handleCancel}
            disabled={busy}
            className="text-sm text-[var(--fcc-muted)] hover:underline"
          >
            Cancel subscription
          </button>
        </div>
      )}
    </div>
  );
}
